/**
 * The one product search on the client (refonte WP-37 / cross-module XWP-12; ux/04 §3, §7.1; A-11).
 *
 * The matching and the ranking belong to the server engine (WP-13: `GET /produits/search` for the
 * counters, `GET /produits?search=` for the lists — token-AND, accent / case-insensitive, EXACT
 * code / N° ENR first, typo tier last). The sellable figure of a counter row is the availability
 * layer's (cross-module XWP-01 / XWP-03 `disponibiliteCaisse`: `qte_totale` = units the user can
 * sell, `disponibilite` VISIBLE / STOCK / RUPTURE). This module only turns those rows into the
 * Combobox options of the ProductPicker presets, and explains a hit (matched fields, tier, the
 * DCI-only generics tier). Pure, tested in lib/__tests__/recherche-produit.test.js.
 */
import { RECHERCHE_MIN_CARACTERES } from "@pharmakon/shared/search";
import {
  LIBELLE_CHAMP,
  LIBELLE_GENERIQUE_DCI,
  LIBELLE_NIVEAU,
} from "@/config/recherche-produit.messages.js";

/** Sale state of a counter row. */
export const ETAT_VENTE = Object.freeze({
  VENDABLE: "VENDABLE", // cashier-visible sellable units
  RESERVE: "RESERVE", // sellable units, only in magasins hidden from this user (shown, locked)
  RUPTURE: "RUPTURE", // nothing sellable (empty, expired, blocked or held by caisse windows)
  BLOQUE: "BLOQUE", // product administratively blocked (BLOQUE_VISIBLE — shown, never sellable)
});

/** Fields every counter row displays: a hit on them needs no « Trouvé par » explanation. */
const CHAMPS_AFFICHES_POS = Object.freeze(["nom_commercial", "dosage", "dci", "forme", "laboratoire"]);

/** @returns {string[]} the rows of a search answer, whatever its envelope. */
export function lignesReponse(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

/** True when the trimmed query reaches the engine's minimum (A-11). */
export function rechercheValide(q) {
  return String(q ?? "").trim().length >= RECHERCHE_MIN_CARACTERES;
}

/** Sale state of a `GET /produits/search` row. */
export function etatVente(prod) {
  if (prod?.statut_produit === "BLOQUE_VISIBLE") return ETAT_VENTE.BLOQUE;
  const d = prod?.disponibilite;
  if (d === "STOCK") return ETAT_VENTE.RESERVE;
  if (d === "RUPTURE") return ETAT_VENTE.RUPTURE;
  if (d === "VISIBLE") return ETAT_VENTE.VENDABLE;
  // offline snapshot rows without the availability read: the sellable total decides
  return Number(prod?.qte_totale ?? 0) > 0 ? ETAT_VENTE.VENDABLE : ETAT_VENTE.RUPTURE;
}

/** « Nom · dosage » — the line 1 of a product row (the label put in the field on pick). */
export function libelleProduitRecherche(p) {
  const nom = String(p?.nom_commercial ?? p?.nom ?? "").trim();
  const dosage = String(p?.dosage ?? "").trim();
  return dosage && !nom.toLowerCase().includes(dosage.toLowerCase()) ? `${nom} · ${dosage}` : nom;
}

const nomRef = (v) => (v && typeof v === "object" ? v.nom ?? v.libelle ?? null : v ?? null);

/**
 * Why a row is in the list (ux/04 §3.2 « match explanation »): the tier word when it is the reason
 * (EXACT code, PROCHE spelling), the matched fields the row does not display (a barcode, the N° ENR,
 * the conditionnement…), and the generics tier — a hit by its DCI alone (« Même DCI »), so a cashier
 * who typed a brand never mistakes a generic for it.
 *
 * @param {{ niveau?: string, champs?: string[] } | null | undefined} pertinence
 * @param {{ affiches?: readonly string[] }} [opts]
 * @returns {{ niveau: string|null, champs: string[], generique: string|null, texte: string|null }}
 */
export function explicationCorrespondance(pertinence, { affiches = CHAMPS_AFFICHES_POS } = {}) {
  if (!pertinence) return { niveau: null, champs: [], generique: null, texte: null };
  const champs = Array.isArray(pertinence.champs) ? pertinence.champs : [];
  const niveau = LIBELLE_NIVEAU[pertinence.niveau] ?? null;
  const caches = champs.filter((c) => !affiches.includes(c)).map((c) => LIBELLE_CHAMP[c] ?? c);
  const parNom = champs.includes("nom_commercial") || champs.includes("marque");
  const generique = champs.includes("dci") && !parNom && pertinence.niveau !== "EXACT" ? LIBELLE_GENERIQUE_DCI : null;
  const morceaux = [];
  if (niveau) morceaux.push(niveau);
  if (caches.length && pertinence.niveau !== "EXACT") morceaux.push(`trouvé par ${caches.join(", ")}`);
  else if (caches.length) morceaux.push(caches.join(", "));
  return { niveau, champs: caches, generique, texte: morceaux.length ? morceaux.join(" — ") : null };
}

/** « mars 2027 » → MM/AA of the next sellable expiry (ux/01 numbers: MM/AA). */
export function moisAnnee(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
}

/**
 * X XWP-96 (brief 2026-10-26 §2, §4; XO-23 (3)) — the counter row's lot line: the FEFO-first counter
 * lot the server put first (`lot_fefo` = `stock_lots[0]`, default magasin first, then FEFO — never
 * re-sorted here), where the box is (`emplacement.libelle`, « Magasin principal · AA-01 », else its
 * magasin) and how many other visible lots follow. Null for a row without counter lots (offline
 * snapshot rows, the catalogue). A CAISSIER's row holds no invisible lot nor a lot of a hidden magasin:
 * the server leaves them out.
 *
 * @param {object} prod  a `GET /produits/search` row
 * @returns {{ id: string, numero_lot: string|null, peremption: string|null, emplacement: string|null, autres: number } | null}
 */
export function resumeLotsComptoir(prod) {
  const lots = Array.isArray(prod?.stock_lots) ? prod.stock_lots : [];
  const tete = prod?.lot_fefo ?? lots[0] ?? null;
  if (!tete || lots.length === 0) return null;
  return {
    id: tete.id,
    numero_lot: tete.numero_lot ?? null,
    peremption: moisAnnee(tete.date_peremption),
    emplacement: tete.emplacement?.libelle ?? tete.magasin_comptoir?.nom ?? null,
    autres: Math.max(0, lots.length - 1),
    // X XWP-98 (XO-25) — the row's identical receptions (P / M answers only; absent for a caissier)
    receptions: Number(tete.nb_receptions ?? 0) > 1 ? Number(tete.nb_receptions) : null,
  };
}

/**
 * A counter row → its Combobox option.
 * `disabled` mirrors what a pick may do: a VENDABLE row is added; a RUPTURE row is pickable only
 * when the screen raises demands (`demande`), then the pick reports the demand for THAT product; a
 * RESERVE / BLOQUE row is listed, locked (the cashier sees why it cannot be sold).
 *
 * @param {object} prod  a `GET /produits/search` row
 * @param {{ demande?: boolean }} [opts]
 */
export function optionPos(prod, { demande = false } = {}) {
  const etat = etatVente(prod);
  const qte = Number(prod?.qte_totale ?? 0);
  return {
    value: String(prod.id),
    label: libelleProduitRecherche(prod),
    meta: [nomRef(prod.laboratoire) ?? prod.laboratoire_nom, nomRef(prod.dci), nomRef(prod.forme)].filter(Boolean).join(" · ") || undefined,
    keywords: prod.num_enr_cnas ?? undefined,
    disabled: etat === ETAT_VENTE.RESERVE || etat === ETAT_VENTE.BLOQUE || (etat === ETAT_VENTE.RUPTURE && !demande),
    etat,
    qte,
    explication: explicationCorrespondance(prod.pertinence),
    produit: prod,
  };
}

/**
 * One counter search: asks `posSearch(q, limit, config)` (services produitApi.posSearch) with the
 * Combobox AbortSignal and maps the answer. A rejected request rejects (the Combobox shows the
 * failure with « Réessayer » — a failed search is never shown as « no product »).
 */
export async function rechercherPos(posSearch, q, { signal, limit, demande = false, typePrefere } = {}) {
  const res = await posSearch(String(q ?? "").trim(), limit, {
    signal,
    ...(typePrefere ? { params: { type_prefere: typePrefere } } : {}),
  });
  return lignesReponse(res).map((row) => optionPos(row, { demande }));
}

/** One catalogue search (`produitApi.search` → `GET /produits?search=`), same contract. */
export async function rechercherCatalogue(search, q, { signal, limit } = {}) {
  const res = await search(String(q ?? "").trim(), limit, { signal });
  return lignesReponse(res).map(optionCatalogue);
}

/**
 * What a pick does on a counter: `ajouter` (sellable), `demande` (rupture on a screen that raises
 * demands), or nothing (`aucune` — locked rows; never a blind add).
 * @returns {"ajouter"|"demande"|"aucune"}
 */
export function actionChoix(option, { demande = false } = {}) {
  if (!option) return "aucune";
  if (option.etat === ETAT_VENTE.VENDABLE) return "ajouter";
  if (option.etat === ETAT_VENTE.RUPTURE && demande) return "demande";
  return "aucune";
}

/** A catalogue row (`GET /produits?search=`) → the option of a non-counter ProductPicker preset. */
export function optionCatalogue(p) {
  return {
    value: String(p.id),
    label: libelleProduitRecherche(p),
    meta:
      [nomRef(p.dci), nomRef(p.forme), nomRef(p.laboratoire) ?? p.laboratoire_nom, p.num_enr_cnas ? `N° ENR ${p.num_enr_cnas}` : null]
        .filter(Boolean)
        .join(" · ") || undefined,
    explication: explicationCorrespondance(p.pertinence, { affiches: ["nom_commercial", "dosage", "dci", "forme", "laboratoire", "num_enr_cnas"] }),
    produit: p,
  };
}
